import type { BiomassDataRow } from './productionUnitTable'

import { deriveObservedMortalityMetrics } from './mortalityProjection'
import { endOfDayStockFromCount } from './productionEndOfDayStock'

/**
 * Read-only "daily mortality %" column for the Database (Datatable) screen —
 * Track A finding 3.1: the prospect wants real-time mortality visible to react
 * fast, and it is not shown anywhere on that screen today.
 *
 * The rate for a `(lot, calendar day)` is the day's canonical mortality count
 * (already MORT-12-rolled-up on `row.dailyMortality` by the overlay chain)
 * divided by the lot's stock at the *start* of that day — i.e. the displayed
 * end-of-day stock carried over from the previous day with data. This mirrors
 * `computeDisplayedEndOfDayStockByRowId` (same grouping, same day-dedup, same
 * "stored stock supersedes" rule) and `deriveObservedMortalityMetrics` (the
 * `count / previousFishStock * 100` formula the Biomass and Dashboard screens
 * already use), so the three surfaces agree.
 *
 * The first day of a lot's history has no baseline, so its rate is `null`
 * (not shown) — matching the Biomass KPI table's `index === 0` behaviour.
 *
 * Pure and side-effect free. Feed it the overlay-composed rows
 * (`rowsWithMortalityOverlay`), not the raw editable `data`.
 */
export type DailyMortalityRate = {
  /** Observed daily mortality %, or `null` when it cannot be derived. */
  ratePct: number | null
  /** The day's canonical mortality count, or `null` when absent/invalid. */
  mortalityCount: number | null
  /** Lot stock at the start of the row's calendar day, or `null`. */
  startOfDayStock: number | null
}

const parseNumeric = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  const parsed = Number.parseFloat(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

const toCalendarDayKey = (value: unknown): string => {
  if (typeof value !== 'string') return ''
  const trimmed = value.trim()
  if (!trimmed) return ''
  const isoMatch = /^(\d{4}-\d{2}-\d{2})/.exec(trimmed)
  if (isoMatch) return isoMatch[1]
  const parsed = Date.parse(trimmed)
  if (!Number.isFinite(parsed)) return ''
  const d = new Date(parsed)
  const yyyy = d.getUTCFullYear().toString().padStart(4, '0')
  const mm = (d.getUTCMonth() + 1).toString().padStart(2, '0')
  const dd = d.getUTCDate().toString().padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

const resolveGroupKey = (row: BiomassDataRow): string => {
  const lotId = String(row.lotId ?? '').trim()
  if (lotId) return `lot-id:${lotId}`
  const lotKey = String(row.lot ?? '').trim()
  if (lotKey) return `lot-key:${lotKey.toLowerCase()}`
  return ''
}

const compareByDateAscThenId = (left: BiomassDataRow, right: BiomassDataRow): number => {
  const leftTime = Date.parse(String(left.date ?? ''))
  const rightTime = Date.parse(String(right.date ?? ''))
  const leftValid = Number.isFinite(leftTime)
  const rightValid = Number.isFinite(rightTime)

  if (leftValid && rightValid && leftTime !== rightTime) return leftTime - rightTime
  if (leftValid && !rightValid) return -1
  if (!leftValid && rightValid) return 1

  return String(left.id ?? '').localeCompare(String(right.id ?? ''))
}

export const computeDailyMortalityRateByRowId = (
  rows: ReadonlyArray<BiomassDataRow>
): Map<string, DailyMortalityRate> => {
  const result = new Map<string, DailyMortalityRate>()
  if (!Array.isArray(rows) || rows.length === 0) return result

  const grouped = new Map<string, BiomassDataRow[]>()
  for (const row of rows) {
    if (!row || typeof row.id !== 'string' || !row.id) continue
    const key = resolveGroupKey(row)
    if (!key) {
      result.set(row.id, { ratePct: null, mortalityCount: null, startOfDayStock: null })
      continue
    }
    const bucket = grouped.get(key)
    if (bucket) bucket.push(row)
    else grouped.set(key, [row])
  }

  grouped.forEach((groupRows) => {
    const sorted = [...groupRows].sort(compareByDateAscThenId)
    let lastKnownStock: number | null = null
    let currentDayKey: string | null = null
    let startOfDayStock: number | null = null
    let dayMortalityApplied = false

    for (const row of sorted) {
      const rowDayKey = toCalendarDayKey(row.date)
      if (rowDayKey !== currentDayKey) {
        currentDayKey = rowDayKey
        startOfDayStock = lastKnownStock
        dayMortalityApplied = false
      }

      const mortalityCount = parseNumeric(row.dailyMortality)
      const storedStock = parseNumeric(row.fishStock)

      // v2.2 #32/#35: a lot's very first stock event (no prior stock at
      // all — lastKnownStock still null, not just "no reading today") is
      // a receiving/intake count, not a recount, so its own same-day
      // mortality is a real, standards-backed rate against the received
      // count (ASC Appendix 12.5.1 rates mortality against stocking
      // count) — not "no baseline yet." Mirrors the netting
      // computeDisplayedEndOfDayStockByRowId already applies to the
      // stock figure itself for the same row.
      const isReceivingEvent: boolean = lastKnownStock === null && storedStock !== null
      // Findings #203: the end-of-day stock comes from the shared count rule;
      // on a receiving day the stock at the start of the day is that plus the
      // day's deaths (the received count when no time says otherwise).
      const countEndOfDay: number | null = storedStock !== null
        ? endOfDayStockFromCount(row, storedStock, isReceivingEvent)
        : null
      const receivingBaseline: number | null = isReceivingEvent && countEndOfDay !== null
        ? countEndOfDay + (mortalityCount !== null && mortalityCount > 0 ? mortalityCount : 0)
        : null
      const rateBaseline = isReceivingEvent ? receivingBaseline : startOfDayStock

      const observed = deriveObservedMortalityMetrics({
        previousFishStock: rateBaseline,
        dailyMortality: mortalityCount
      })

      result.set(row.id, {
        ratePct: observed ? observed.observedMortalityRate : null,
        mortalityCount: mortalityCount !== null && mortalityCount >= 0 ? mortalityCount : null,
        startOfDayStock: isReceivingEvent ? receivingBaseline : startOfDayStock
      })

      // Advance the running stock exactly as computeDisplayedEndOfDayStockByRowId
      // does: a stored fishStock supersedes; otherwise deduct the day's mortality
      // once. On a receiving event, net this same day's mortality out of the
      // carried-forward value too — not just the rate above — or every later
      // day's own rate silently rates against the un-netted raw count again
      // (caught live: day 2 computed 5/10000 instead of 5/9400).
      if (countEndOfDay !== null) {
        lastKnownStock = countEndOfDay
        dayMortalityApplied = true
        continue
      }

      if (lastKnownStock !== null && mortalityCount !== null && mortalityCount >= 0) {
        const deduction = dayMortalityApplied ? 0 : mortalityCount
        lastKnownStock = Math.max(0, lastKnownStock - deduction)
        dayMortalityApplied = true
      }
    }
  })

  return result
}
